import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Sidebar from '@/components/Sidebar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, Upload, Image as ImageIcon } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Settings() {
  const [logo, setLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [currentLogo, setCurrentLogo] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      if (response.data.logo_url) {
        setCurrentLogo(response.data.logo_url);
      }
    } catch (error) {
      console.error('Failed to fetch settings');
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogo(file);
      setLogoPreview(URL.createObjectURL(file));
    }
  };

  const handleUploadLogo = async (e) => {
    e.preventDefault();
    if (!logo) {
      toast.error('Please select a logo file');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('logo', logo);

      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/settings/logo`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      setCurrentLogo(response.data.logo_url);
      setLogo(null);
      setLogoPreview(null);
      toast.success('Logo uploaded successfully!');
    } catch (error) {
      toast.error('Failed to upload logo');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-zinc-950" data-testid="settings-page">
      <Sidebar />
      <main className="flex-1 p-8">
        <div className="max-w-4xl mx-auto">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="mb-6 text-zinc-400 hover:text-white hover:bg-zinc-800"
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>

          <h1 className="text-4xl font-bold text-white mb-2" data-testid="settings-title">Settings</h1>
          <p className="text-zinc-400 mb-8" data-testid="settings-description">Configure your admin panel settings</p>

          <Card className="border-zinc-800 bg-zinc-900/50">
            <CardContent className="p-6">
              <h2 className="text-xl font-semibold text-white mb-6" data-testid="logo-settings-title">Logo Settings</h2>
              
              {currentLogo && (
                <div className="mb-6">
                  <Label className="text-zinc-300 mb-2 block">Current Logo</Label>
                  <div className="p-4 rounded-lg bg-zinc-800 inline-block" data-testid="current-logo">
                    <img src={currentLogo} alt="Current Logo" className="max-h-24 max-w-full" />
                  </div>
                </div>
              )}

              <form onSubmit={handleUploadLogo} className="space-y-4" data-testid="logo-upload-form">
                <div className="space-y-2">
                  <Label htmlFor="logo" className="text-zinc-300">Upload New Logo</Label>
                  <Input
                    id="logo"
                    type="file"
                    accept=".png,.jpg,.jpeg,.svg,.webp"
                    onChange={handleLogoChange}
                    className="bg-zinc-800 border-zinc-700 text-white file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:bg-blue-600 file:text-white hover:file:bg-blue-700 file:cursor-pointer"
                    data-testid="logo-input"
                  />
                </div>

                {logoPreview && (
                  <div className="p-4 rounded-lg bg-zinc-800 inline-block" data-testid="logo-preview">
                    <img src={logoPreview} alt="Logo Preview" className="max-h-24 max-w-full" />
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={loading || !logo}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white"
                  data-testid="upload-logo-button"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {loading ? 'Uploading...' : 'Upload Logo'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
